// ---------------------------------------------------------------------------
// useSeo / useJsonLd — the only two places in the app that write to <head>.
//
// No framework, by design: react-helmet and its successors exist to reconcile
// competing head declarations across a deep tree, and ReCurr has one declaration
// per route (config/seo.ts) plus two blocks of structured data. A dependency
// that solves a problem the app does not have is a dependency that has to be
// upgraded for ever.
//
// Everything here UPSERTS and nothing appends. A meta tag written twice is not
// twice as strong — it is ambiguous, and crawlers pick whichever they like.
// Tags are matched on the same attribute that identifies them to a crawler
// (`name` for the standard and Twitter ones, `property` for Open Graph), so a
// tag that index.html already ships is UPDATED rather than duplicated. That
// matters more here than in most apps: index.html carries a complete set of
// defaults for the previewers that never run this code at all.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import {
  OG_IMAGE_ALT,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_PATH,
  OG_IMAGE_WIDTH,
  PageSeo,
  SITE_LOCALE,
  SITE_NAME,
  absoluteUrl,
} from '../config/seo';

type MetaKey = 'name' | 'property';

function setMeta(key: MetaKey, id: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${id}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, id);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(key: MetaKey, id: string): void {
  document.head.querySelector(`meta[${key}="${id}"]`)?.remove();
}

function setCanonical(href: string | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    existing?.remove();
    return;
  }
  const el = existing ?? document.createElement('link');
  el.setAttribute('rel', 'canonical');
  el.setAttribute('href', href);
  if (!existing) document.head.appendChild(el);
}

/**
 * Write one page's head tags. Idempotent — calling it again with the same
 * values changes nothing, which is what makes it safe on every navigation.
 */
export function applySeo(seo: PageSeo): void {
  document.title = seo.title;
  setMeta('name', 'description', seo.description);

  const canonical = seo.canonicalPath === null ? null : absoluteUrl(seo.canonicalPath);
  setCanonical(canonical);

  const ogTitle = seo.ogTitle ?? seo.title;
  const ogDescription = seo.ogDescription ?? seo.description;

  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:locale', SITE_LOCALE);
  // `article` on a blog post, `website` everywhere else — a share of an
  // article is a share of a piece of writing, and previewers treat the two
  // differently. The default keeps every existing page exactly as it was.
  setMeta('property', 'og:type', seo.ogType ?? 'website');
  setMeta('property', 'og:title', ogTitle);
  setMeta('property', 'og:description', ogDescription);
  setMeta('property', 'og:image', absoluteUrl(OG_IMAGE_PATH));
  setMeta('property', 'og:image:width', String(OG_IMAGE_WIDTH));
  setMeta('property', 'og:image:height', String(OG_IMAGE_HEIGHT));
  setMeta('property', 'og:image:alt', OG_IMAGE_ALT);

  // og:url is the share card's own identity. With no canonical there is no
  // honest value for it either, so it goes rather than pointing somewhere else.
  if (canonical) setMeta('property', 'og:url', canonical);
  else removeMeta('property', 'og:url');

  // X/Twitter reads og:* for most fields but needs its own card type, and
  // `summary_large_image` is the difference between a 1200×630 picture and a
  // thumbnail beside two lines of text.
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', ogTitle);
  setMeta('name', 'twitter:description', ogDescription);
  setMeta('name', 'twitter:image', absoluteUrl(OG_IMAGE_PATH));
  setMeta('name', 'twitter:image:alt', OG_IMAGE_ALT);
}

/** Apply a page's head tags for as long as the component is mounted. */
export function useSeo(seo: PageSeo): void {
  useEffect(() => {
    applySeo(seo);
    // Deliberately keyed on the VALUES rather than the object: config/seo.ts
    // returns a fresh object for the legal routes, and an object dependency
    // would rewrite the whole head on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seo.title, seo.description, seo.canonicalPath, seo.ogTitle, seo.ogDescription, seo.ogType]);
}

/**
 * Structured data, one <script> per `id`.
 *
 * Keyed by id and never appended, so a page can publish several blocks and a
 * re-render replaces rather than accumulates. Passing `null` removes the block,
 * which is how a page that is still loading its data avoids publishing an empty
 * or half-true one — structured data that disagrees with the visible page is a
 * manual-action risk, not just a wasted tag.
 *
 * Read by search engines, which execute JavaScript. NOT read by social link
 * previewers, which do not — that is what the static tags in index.html are for,
 * and why the Organization block lives there rather than here.
 */
export function useJsonLd(id: string, data: unknown | null): void {
  const json = data === null ? null : JSON.stringify(data);

  useEffect(() => {
    const elementId = `ld-${id}`;
    const existing = document.getElementById(elementId);

    if (json === null) {
      existing?.remove();
      return;
    }

    const el = existing ?? document.createElement('script');
    if (!existing) {
      el.id = elementId;
      (el as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = json;

    return () => {
      document.getElementById(elementId)?.remove();
    };
  }, [id, json]);
}

/**
 * Mark the current page `noindex` for as long as it is mounted.
 *
 * For the SPA soft-404. A single-page app cannot answer a wrong URL with an
 * HTTP 404 — the server has already sent 200 and index.html by the time the
 * router discovers nothing matches — so /a-typo, /old-bookmark and every stale
 * inbound link is a 200-status page saying "not found". Search engines guess at
 * these ("soft 404") and guess wrong often enough to index them. A robots meta
 * tag is the one instruction that is unambiguous, and it is removed on unmount
 * so navigating away from the 404 does not leave the rest of the site noindexed.
 *
 * A real 404 status would need the server to know the route table, which means
 * pre-rendering. This is the honest client-side half of that.
 */
export function useNoIndex(): void {
  useEffect(() => {
    setMeta('name', 'robots', 'noindex, follow');
    return () => removeMeta('name', 'robots');
  }, []);
}
